import * as React from 'react'
import { CornerDownLeft, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/components/visitone/visitone-provider'
import { DESTINATIONS, type DestinationId } from '@/components/visitone/navigation-model'
import synonymDestinations from '@/lib/i18n/catalogues/synonym-destinations.json'
import synonyms from '@/lib/i18n/catalogues/synonyms.en-GB.json'
import { SETUP_ORDER_TYPES_PATH } from '@/lib/routing'

/** A searchable palette entry that opens an in-app route, optionally with router state. */
export type PaletteRoute = {
  key: string
  label: string
  route: string
  state?: unknown
}

/** A plan-limited feature the caller holds (VIS-527). */
export type PaletteFeature = {
  key: string
  label: string
  destination: DestinationId
  route: string
}

type PaletteItem = {
  key: string
  label: string
  note?: string
  destination?: DestinationId
  route?: string
  state?: unknown
}

type PaletteGroup = { heading: string; items: PaletteItem[] }

const synonymIndex = synonyms as Record<string, string[]>
/** The destination each canonical synonym target belongs to; an unmapped target is never offered. */
const synonymDestination = synonymDestinations as Record<string, DestinationId>

/*
 * Command palette: one keyboard-first input. Canonical labels and locale
 * synonyms are searchable; results always display canonical language. The
 * empty state shows useful recents before the user types anything. Pages,
 * records, recents and synonyms inside destinations the caller may not open
 * are never offered (VIS-329).
 */
export function CommandPalette({
  open,
  onClose,
  onNavigate,
  onOpenRoute,
  destinations,
  routes = [],
  features = [],
}: {
  open: boolean
  onClose: () => void
  onNavigate?: (id: DestinationId) => void
  onOpenRoute?: (route: string, state?: unknown) => void
  /** Permitted destinations. */
  destinations: DestinationId[]
  /** Route entries the caller may open, e.g. Setup screens they have access to. */
  routes?: PaletteRoute[]
  features?: PaletteFeature[]
}) {
  if (!open) return null
  return (
    <PaletteDialog
      onClose={onClose}
      onNavigate={onNavigate}
      onOpenRoute={onOpenRoute}
      destinations={destinations}
      routes={routes}
      features={features}
    />
  )
}

/*
 * The open palette: a native modal <dialog>, so focus stays inside it and the
 * page behind is inert. Esc, the backdrop and choosing a result close it, and
 * focus returns to whatever opened it. Results are options of the combobox's
 * listbox; the input keeps focus and the active option is announced through
 * `aria-activedescendant`.
 */
function PaletteDialog({
  onClose,
  onNavigate,
  onOpenRoute,
  destinations,
  routes,
  features,
}: {
  onClose: () => void
  onNavigate?: (id: DestinationId) => void
  onOpenRoute?: (route: string, state?: unknown) => void
  destinations: DestinationId[]
  routes: PaletteRoute[]
  features: PaletteFeature[]
}) {
  const t = useT()
  const [query, setQuery] = React.useState('')
  const [activeIndex, setActiveIndex] = React.useState(0)
  const dialogRef = React.useRef<HTMLDialogElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const listId = React.useId()
  const optionId = (index: number) => `${listId}-option-${index}`

  // A layout effect, so the dialog is closed while still in the document: until
  // it closes, the page behind is inert and the opener cannot take focus.
  React.useLayoutEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const dialog = dialogRef.current
    if (dialog && !dialog.open) dialog.showModal()
    inputRef.current?.focus()
    return () => {
      if (dialog?.open) dialog.close()
      if (opener?.isConnected) opener.focus()
    }
  }, [])

  const groups = React.useMemo<PaletteGroup[]>(() => {
    const term = query.trim().toLowerCase()
    const permitted = (destination: DestinationId) => destinations.includes(destination)

    if (!term) {
      const recents: [DestinationId, PaletteItem][] = [
        ['guests', { key: 'recent-guests', label: t('core.nav.guests'), destination: 'guests' }],
        ['programme', { key: 'recent-fav', label: t('core.nav.favouriteTonight') }],
        ['setup', { key: 'recent-order-types', label: t('core.nav.orderTypes'), route: SETUP_ORDER_TYPES_PATH }],
      ]
      const items = recents.filter(([destination]) => permitted(destination)).map(([, item]) => item)
      return items.length ? [{ heading: t('core.nav.paletteRecents'), items }] : []
    }

    const pageMatches: PaletteItem[] = DESTINATIONS.filter(
      (destination) => permitted(destination.id) && t(destination.labelKey).toLowerCase().includes(term),
    ).map((destination) => ({
      key: destination.id,
      label: t(destination.labelKey),
      destination: destination.id,
    }))

    const routeMatches: PaletteItem[] = routes.filter((entry) => entry.label.toLowerCase().includes(term))

    const featureMatches: PaletteItem[] = features
      .filter((feature) => permitted(feature.destination) && feature.label.toLowerCase().includes(term))
      .map(({ key, label, destination, route }) => ({ key, label, destination, route }))

    const synonymMatches: PaletteItem[] = Object.entries(synonymIndex)
      .filter(([deprecated]) => deprecated.includes(term))
      .flatMap(([deprecated, canonical]) =>
        canonical
          .filter((label) => {
            const destination = synonymDestination[label]
            return destination !== undefined && permitted(destination)
          })
          .map((label) => ({
            key: `${deprecated}-${label}`,
            label,
            note: t('core.nav.paletteMatched', { term: deprecated }),
          })),
      )
      .slice(0, 5)

    const results = [...pageMatches, ...routeMatches, ...featureMatches, ...synonymMatches]
    const groupList: PaletteGroup[] = []
    if (results.length) groupList.push({ heading: t('core.nav.results'), items: results })
    return groupList
  }, [query, destinations, routes, features, t])

  const flatItems = React.useMemo(() => groups.flatMap((group) => group.items), [groups])

  React.useEffect(() => setActiveIndex(0), [query])

  React.useEffect(() => {
    document.getElementById(optionId(activeIndex))?.scrollIntoView?.({ block: 'nearest' })
  })

  const activate = (item: PaletteItem) => {
    if (item.route) onOpenRoute?.(item.route, item.state)
    else if (item.destination) onNavigate?.(item.destination)
    onClose()
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => Math.min(index + 1, Math.max(flatItems.length - 1, 0)))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const item = flatItems[activeIndex]
      if (item) activate(item)
    }
  }

  let runningIndex = -1
  const hasResults = flatItems.length > 0

  return (
    <dialog
      ref={dialogRef}
      aria-label={t('core.nav.paletteTitle')}
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onClick={(event) => {
        if (event.target === dialogRef.current) onClose()
      }}
      className={cn(
        'mx-auto mt-[min(12vh,6rem)] mb-0 flex max-h-[calc(100dvh-min(12vh,6rem)-0.5rem)] w-[min(36rem,calc(100vw-1rem))] max-w-none flex-col',
        'overflow-hidden rounded-lg border border-line bg-surface p-0 text-fg shadow-e3 backdrop:bg-[rgb(8_19_14_/_45%)]',
      )}
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-line px-3">
        <Search aria-hidden="true" className="size-5 shrink-0 text-fg-muted" />
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-label={t('core.nav.paletteTitle')}
          aria-autocomplete="list"
          aria-expanded={hasResults}
          aria-controls={hasResults ? listId : undefined}
          aria-activedescendant={flatItems[activeIndex] ? optionId(activeIndex) : undefined}
          placeholder={t('core.nav.paletteHint')}
          className="h-12 min-w-0 flex-1 bg-transparent text-sm text-fg placeholder:text-fg-muted focus-visible:-outline-offset-2"
        />
        <kbd className="shrink-0 rounded-sm border border-line px-1.5 py-0.5 text-xs text-fg-muted">esc</kbd>
      </div>

      {hasResults ? (
        <div id={listId} role="listbox" aria-label={t('core.nav.paletteTitle')} className="max-h-80 min-h-0 overflow-auto p-2">
          {groups.map((group, groupIndex) => {
            const headingId = `${listId}-group-${groupIndex}`
            return (
              <div key={group.heading} role="group" aria-labelledby={headingId} className="mb-1">
                <p id={headingId} className="px-2 py-1.5 text-xs tracking-wide text-fg-muted">
                  {group.heading.toUpperCase()}
                </p>
                {group.items.map((item) => {
                  runningIndex += 1
                  const index = runningIndex
                  const active = index === activeIndex
                  return (
                    <div
                      key={item.key}
                      id={optionId(index)}
                      role="option"
                      aria-selected={active}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => activate(item)}
                      className={cn(
                        'flex min-h-10 w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-2 text-left text-sm',
                        active ? 'bg-selected text-fg-strong' : 'text-fg hover:bg-surface-hover',
                      )}
                    >
                      <span className="min-w-0 wrap-anywhere">{item.label}</span>
                      {item.note ? (
                        <span className="ml-auto min-w-0 text-right text-xs text-fg-muted wrap-anywhere">{item.note}</span>
                      ) : null}
                      {active ? (
                        <CornerDownLeft aria-hidden="true" className="size-4 shrink-0 text-fg-muted" />
                      ) : null}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      ) : (
        <p role="status" className="px-3 py-6 text-center text-sm text-fg-muted">
          {t('core.nav.paletteNoResults')}
        </p>
      )}
    </dialog>
  )
}
